import { PackageType } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import { SlotWorkflowClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { StockReplenishmentExtended } from '@frontend/stock/types';
import { TransactionActorClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionActorType, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';

import { useWizardPackages } from '../../hooks/useWizardPackages';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

export interface ReplenishmentWithProduct extends Omit<StockReplenishmentExtended, 'product'> {
    product: IProduct;
}

export const useReplenishDetail = () => {
    const { navigationState, store, onSubmit: onContinue, onBack, updateWizardData } = useScreen();
    const [amount, setAmount] = useState<number | null>(null);
    const [availableSlots, setAvailableSlots] = useState<Slot[] | null>(null);

    // Get the selected product from wizard store
    const replenishment = store?.data?.selectedProduct as ReplenishmentWithProduct | undefined;

    // Initialize transaction context
    const transactionContext = useWizardTransaction({
        store: store,
        wizardId: store?.wizard.id
    });

    const { setPackages, syncPackages } = useWizardPackages({
        store: store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        transactionContext
    });

    useEffect(() => {
        if (!availableSlots || !replenishment) return;
        if (amount === null || amount > availableSlots.length) {
            if (availableSlots.length < replenishment.proposed_quantity) {
                setAmount(availableSlots.length);
            } else {
                setAmount(replenishment.proposed_quantity);
            }
        }
    }, [availableSlots, replenishment, amount]);

    useEffect(() => {
        if (!replenishment) return;
        SlotWorkflowClient.postAvailableSlots(replenishment.account_id, replenishment.spot_id, {
            account_id: replenishment.account_id,
            application: store?.functionality?.application,
            feature: store?.functionality?.feature,
            functionality_id: store?.functionality?.id,
            product_id: replenishment.product_id,
            product_type: replenishment.product.type,
            fill_algorithm: replenishment.fill_algorithm,
            transaction_type: TransactionType.VENDING
        }).then((result) => {
            setAvailableSlots(result.results);
        });
    }, [replenishment]);

    async function replenish() {
        if (!store || !navigationState.iot || !navigationState.user || !replenishment || !availableSlots || amount === null || amount <= 0) return;

        const transactionWorkflowId = store.functionalitySettings.find((setting) => setting.key === SettingKey.TRANSACTION_WORKFLOW_ID)?.value;
        const packageWorkflowId = store.functionalitySettings.find((setting) => setting.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID)?.value;
        if (typeof transactionWorkflowId !== 'string' || typeof packageWorkflowId !== 'string') return;

        const transaction = await transactionContext.syncTransaction({
            type: TransactionType.VENDING,
            workflow_id: transactionWorkflowId,
            spot_id: navigationState.iot.spot_id,
            user_id: navigationState.user.id,
            iot_id: navigationState.iot.id,
            application: store.functionality.application,
            feature: store.functionality.feature,
            state: TransactionStateName.TRANSACTION_CREATED
        });

        const max = Math.min(amount, availableSlots.length);
        if (max <= 0) return;

        await setPackages(
            availableSlots.slice(0, max).map((slot, index) => ({
                workflow_id: packageWorkflowId,
                user_id: transaction.user_id,
                transaction_id: transaction.id,
                quantity: 1,
                type: PackageType.DROP_OFF,
                spot_id: slot.spot_id,
                module_id: slot.module_id,
                slot_id: slot.id,
                product_id: replenishment.product_id,
                priority: max - index,
                blocking: false,
                application: store.functionality.application,
                feature: store.functionality.feature
            }))
        );

        await syncPackages();

        await TransactionWorkflowClient.triggerTransactionState(transaction.account_id!, transaction.id!, {
            trigger: TransactionTriggerName.TRANSACTION_PROCESS,
            location: navigationState.spot?.id ?? undefined,
            actor: navigationState.user?.id ?? undefined
        });

        if (navigationState.user?.id) {
            await TransactionActorClient.postTransactionActor(transaction.account_id!, transaction.id!, {
                type: TransactionActorType.DROPPED_OFF_BY,
                user_id: navigationState.user.id
            });
        }
        const nextStep = store?.steps.find((step) => step.screen === ScreenType.REFILL_DETAIL && step.trigger === ScreenTrigger.SUBMIT);
        onContinue(nextStep);
    }

    const getMaxAmount = () => {
        if (availableSlots === null || !replenishment) return 0;
        return Math.min(replenishment.proposed_max, availableSlots.length);
    };

    const handleBack = () => {
        updateWizardData({ selectedProduct: null });
        onBack();
    };

    return {
        replenishment,
        amount,
        setAmount,
        replenish,
        getMaxAmount,
        onBack: handleBack
    };
};
